/* Routes shared by both the Lumetier and the Allure brand surface.
   These all live under /admin, /gallery, /s, etc. — the SaaS product itself,
   which is the same product regardless of which domain you reach it from. */

import { Route } from "react-router-dom";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminLayout from "@/components/AdminLayout";

import AdminLogin from "@/pages/AdminLogin";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/admin/Dashboard";
import AdminBookings from "@/pages/admin/Bookings";
import AdminServices from "@/pages/admin/AdminServices";
import AdminInvoices from "@/pages/admin/Invoices";
import AdminCustomers from "@/pages/admin/Customers";
import AdminInquiries from "@/pages/admin/Inquiries";
import AdminHelp from "@/pages/admin/Help";
import AdminSite from "@/pages/admin/Site";
import AdminAvailability from "@/pages/admin/Availability";
import AdminContracts from "@/pages/admin/Contracts";
import AdminGalleries from "@/pages/admin/Galleries";
import AdminGalleryDetail from "@/pages/admin/GalleryDetail";
import AdminGalleryClientDetail from "@/pages/admin/GalleryClientDetail";
import SignContract from "@/pages/SignContract";
import PublicGallery from "@/pages/Gallery";
import InquiryReply from "@/pages/InquiryReply";
import PublicReview from "@/pages/Review";
import PreviewKit from "@/pages/PreviewKit";
import AdminReviews from "@/pages/admin/Reviews";
import AdminWaitlist from "@/pages/admin/Waitlist";
import AdminStudioInvites from "@/pages/admin/StudioInvites";
import AdminProfile from "@/pages/admin/Profile";
import AdminBilling from "@/pages/admin/Billing";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const PLATFORM_OWNER_STUDIO_ID = "allure-image";

/* Wrap routes that only the founding studio (Allure) may visit. Tenants who
   guess the URL or hit a stale bookmark get redirected to their own /admin
   instead of seeing a half-broken page that 403s on every API call. */
function PlatformOwnerOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user?.studio_id !== PLATFORM_OWNER_STUDIO_ID) {
    return <Navigate to="/admin" replace />;
  }
  return children;
}

/* React Router supports rendering Route children as a function call — we
   inline the JSX in both brand trees by calling renderSharedRoutes() so
   each <Routes> block remains a flat list of <Route> children (no fragments,
   no nested Routes). */
export function renderSharedRoutes() {
  return (
    <>
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/forgot-password" element={<ForgotPassword />} />
      <Route path="/admin/reset-password" element={<ResetPassword />} />
      <Route path="/admin/auth/callback" element={<AuthCallback />} />
      <Route path="/gallery/:slug" element={<PublicGallery />} />
      {/* G5 — live session deep-link route. PublicGallery reads roomId via useParams. */}
      <Route path="/gallery/:slug/live/:roomId" element={<PublicGallery />} />
      <Route path="/contract/sign/:token" element={<SignContract />} />
      <Route path="/review/:token" element={<PublicReview />} />
      <Route path="/inquiry/:token" element={<InquiryReply />} />
      {/* Dev-only design-system preview kit. Gated to non-prod or ?kit=show. */}
      <Route path="/preview-kit" element={<PreviewKit />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="bookings" element={<AdminBookings />} />
        <Route path="services" element={<AdminServices />} />
        <Route path="invoices" element={<AdminInvoices />} />
        <Route path="customers" element={<AdminCustomers />} />
        <Route path="inquiries" element={<AdminInquiries />} />
        <Route path="help" element={<AdminHelp />} />
        <Route path="availability" element={<AdminAvailability />} />
        <Route path="contracts" element={<AdminContracts />} />
        <Route path="galleries" element={<AdminGalleries />} />
        <Route path="galleries/clients/:clientId" element={<AdminGalleryClientDetail />} />
        <Route path="galleries/:id" element={<AdminGalleryDetail />} />
        <Route path="reviews" element={<AdminReviews />} />
        <Route path="waitlist" element={<PlatformOwnerOnly><AdminWaitlist /></PlatformOwnerOnly>} />
        <Route path="studio-invites" element={<PlatformOwnerOnly><AdminStudioInvites /></PlatformOwnerOnly>} />
        <Route path="site" element={<AdminSite />} />
        <Route path="profile" element={<AdminProfile />} />
        <Route path="billing" element={<AdminBilling />} />
      </Route>
    </>
  );
}
